import { injectIntl } from "react-intl";
import { SkeletonTheme } from "react-loading-skeleton";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { WindowSizeContextProvider } from "./WindowSizeContext";
import { ApiContextProvider } from "./ApiContext";
import { BackendContextProvider } from "./BackendContext";
import { NotifierContextProvider } from "./NotifierContext";
import { BrowserContextProvider } from "./BrowserContext";
import { SessionContextProvider } from "./SessionContext";
import { lcmApiClient, backendApiClient } from "../api";
import { RulesContextProvider } from "./RulesContext";
import { TrackingContextProvider } from "./TrackingContext";
import { LogContextProvider } from "./LogContext";
import { CustomQueryClientProvider } from "./CustomQueryClientContext";
import { Colors } from "../constants";
import SubscriptionGuard from "../components/SubscriptionGuard";

type OwnProps = {
  children?: React.ReactNode;
  enforceSubscription?: boolean;
  subscriptionEndpoints?: string[];
  unsecuredEndpoints?: string[];
};

const defaultProps: Partial<OwnProps> = {
  children: undefined,
  enforceSubscription: false,
  subscriptionEndpoints: undefined,
  unsecuredEndpoints: undefined,
};

type Props = OwnProps & typeof defaultProps;

export function ApplicationContext({
  children,
  enforceSubscription,
  subscriptionEndpoints,
  unsecuredEndpoints,
}: Props) {
  return (
    <LogContextProvider>
      <TrackingContextProvider>
        <RulesContextProvider>
          <BackendContextProvider backendApiClient={backendApiClient}>
            <SessionContextProvider>
              <NotifierContextProvider>
                <ApiContextProvider lcmApiClient={lcmApiClient}>
                  <CustomQueryClientProvider>
                    <ReactQueryDevtools initialIsOpen={false} />
                    <SubscriptionGuard
                      enforceSubscription={enforceSubscription}
                      subscriptionEndpoints={subscriptionEndpoints}
                      unsecuredEndpoints={unsecuredEndpoints}
                    >
                      <BrowserContextProvider>
                        <WindowSizeContextProvider>
                          <SkeletonTheme
                            baseColor={Colors.EH_WINTER_GRAY_1}
                            highlightColor={Colors.EH_WINTER_GRAY_2}
                          >
                            {children}
                          </SkeletonTheme>
                        </WindowSizeContextProvider>
                      </BrowserContextProvider>
                    </SubscriptionGuard>
                  </CustomQueryClientProvider>
                </ApiContextProvider>
              </NotifierContextProvider>
            </SessionContextProvider>
          </BackendContextProvider>
        </RulesContextProvider>
      </TrackingContextProvider>
    </LogContextProvider>
  );
}

ApplicationContext.defaultProps = defaultProps;

// @ts-expect-error TS(2769) FIXME: No overload matches this call.
export default injectIntl(ApplicationContext);
